import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideMessage } from "Store/Reducer/common.slice";
import useMobileViewport from "hooks/useMobileViewport";
import useAppLanguage from "hooks/useAppLanguage";
import Mt5TradeDonePage from "components/trading/Mt5TradeDonePage";
import {
  isTradeSuccessToastMessage,
  looksLikeTradeFailure,
} from "utils/parseTradeSuccessMessage";

function localizeToastMessage(text, t) {
  const raw = text != null ? String(text).replace(/\s+/g, " ").trim() : "";
  if (!raw) return raw;
  if (/^script not found$/i.test(raw)) return t("scriptNotFound");
  if (/position not found or already closed/i.test(raw)) return t("positionNotFoundOrClosed");
  if (
    /request failed with status code 401/i.test(raw) ||
    /^session expired/i.test(raw)
  ) {
    return t("sessionExpired");
  }
  return raw;
}

/**
 * Matches fxbazzar_frontend bar colors; mobile trade success → MT5 full-page Done
 * instead of the strip toast (API message unchanged — display only).
 */
export default function GlobalReduxToast() {
  const { showMessage, message, varient } = useSelector((s) => s.common);
  const dispatch = useDispatch();
  const isMobile = useMobileViewport();
  const { t } = useAppLanguage();

  const msgText = localizeToastMessage(message != null ? String(message) : "", t);
  const isErrorOrder = varient === "errorOrder";
  const useMt5Done =
    Boolean(showMessage) &&
    isMobile &&
    (isErrorOrder || isTradeSuccessToastMessage(msgText, varient));
  const mt5DoneIsError =
    isErrorOrder || (useMt5Done && looksLikeTradeFailure(msgText));

  const dismiss = useCallback(() => {
    dispatch(hideMessage());
  }, [dispatch]);

  useEffect(() => {
    if (!showMessage || useMt5Done) return undefined;
    const timerId = window.setTimeout(() => dispatch(hideMessage()), 6000);
    return () => window.clearTimeout(timerId);
  }, [showMessage, useMt5Done, dispatch]);

  if (!showMessage) return null;

  if (useMt5Done) {
    return (
      <Mt5TradeDonePage
        message={msgText}
        error={mt5DoneIsError}
        onClose={dismiss}
      />
    );
  }

  const isSuccessOrder = varient === "successOrder";
  const showSuccessIcon = varient === "success" || varient === "successOrder";

  const rootClass = [
    "redux-toast",
    isSuccessOrder ? "redux-toast--green" : "",
    "redux-toast--show",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      id="toast_custom"
      role="status"
      aria-live="polite"
      onClick={isMobile ? dismiss : undefined}
    >
      <div className="redux-toast__icon" id="img" aria-hidden>
        <span
          className={`material-icons-outlined ${
            showSuccessIcon ? "redux-toast__symbol--success" : "redux-toast__symbol--alert"
          }`}
        >
          {showSuccessIcon ? "check_circle_outline" : "info"}
        </span>
      </div>
      <div className="redux-toast__msg" id="desc">
        {msgText}
      </div>
    </div>
  );
}
